/**
 * Practice forms — the staff-side request lifecycle client (`tasks/patient-forms.md` §13.2).
 *
 * Wave 3 (C3). Consumed by `PatientFormListComponent` (the patient-file panel) and
 * `PatientFormSendDialogComponent` (the manual send). Same shape as its siblings
 * `patient-consent.service.ts` and `patient-satisfaction-survey.service.ts`: a `BehaviorSubject`
 * keyed on the currently-open patient, refilled by `PatientService.currentPatient$`, and kept
 * live by socket listeners.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ────────────────────────────────────────────────────────────
 *
 * There is NO copy-link method and no place a `tokenDigest` could land. §14.3 decision 2: the raw
 * token is unrecoverable by construction (§4.1) and `toStaffDto` strips the digest server-side, so
 * a practice that needs the patient to hold the link again calls `resendRequest`, which mints a
 * NEW token and kills the old link immediately. Anything on this class that looked like "get me
 * the link" would be a lie.
 *
 * ── THE 409s ARE ANSWERS, NOT ERRORS ─────────────────────────────────────────────────────────
 *
 * `POST /patient-form-requests` answers 409 `OPEN_REQUEST` when one is already live (§2.1), and
 * carries `existingRequestId` / `nextEligibleAt` with it. That is the normal, expected answer to
 * "send this form to this patient" — the caller renders what happened. `describeSendFailure` below
 * is the ONE place that code becomes a sentence, so the dialog and the panel cannot word it
 * differently. (`WITHIN_REPEAT_WINDOW` and `COMPLETED_NO_REPEAT` were here until §30.)
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, switchMap, tap } from 'rxjs';

import { FormAnswers, FormSchema } from '../../shared/models/custom-form.model';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';

/* ── Types. `shared/models/custom-form.model.ts` owns the SCHEMA types (§13.4); these are the
 *    request-lifecycle DTOs, which are Wave 3's and live with their only consumer. ─────────── */

export type PatientFormRequestStatus =
  | 'pending' | 'in_progress' | 'completed' | 'expired' | 'revoked';

/** The `CustomForm` include `loadRequestsByPatient` attaches. */
export interface PatientFormRequestForm {
  id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  requiresSignature?: boolean;
}

/** The pinned version, returned only by `GET /patient-form-requests/:id`. */
export interface PatientFormRequestVersion {
  id: string;
  versionNumber: number;
  schema: FormSchema | null;
  publishedAt?: string | null;
}

/**
 * The staff DTO. NOTE WHAT IS ABSENT: `tokenDigest` is deleted server-side and there is no raw
 * token anywhere — do not add either, and do not render either.
 */
export interface PatientFormRequestDto {
  id: string;
  groupId: string;
  patientId: string;
  customFormId: string;
  customFormVersionId: string;
  requestedByUserId?: string | null;
  physicianId?: string | null;
  source: string;
  status: PatientFormRequestStatus;
  /** Plaintext derived columns — present on the LIST, where `score` itself is stripped. */
  scoreTotal?: number | null;
  scoreMax?: number | null;
  startedAt?: string | null;
  completedAt?: string | null;
  lastSentAt?: string | null;
  resendCount?: number;
  tokenExpiry?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  /** Computed by `toStaffDto`; drives the resend button's disabled state. */
  nextAllowedResendAt?: string | null;
  CustomForm?: PatientFormRequestForm | null;
  /** Only on `GET /patient-form-requests/:id`. */
  CustomFormVersion?: PatientFormRequestVersion | null;
  answers?: FormAnswers | null;
  score?: { total: number; max: number; bySection?: any } | null;
}

/** A `CustomForm` as `GET /custom-forms` returns it — the send dialog's picker. */
export interface SendableCustomForm {
  id: string;
  groupId: string;
  name: string;
  description?: string | null;
  category?: string | null;
  status: string;
  currentVersionId?: string | null;
  allowManualSend?: boolean;
  requiresSignature?: boolean;
  expiryDays?: number | null;
  active?: boolean;
}

export interface CreateFormRequestPayload {
  patientId: string;
  customFormId: string;
  physicianId?: string | null;
  channels: string[];
}

/** The three dedupe codes plus the 422s the dialog filters for but still has to survive. */
export interface FormRequestFailure {
  /** The server's `error` code, or `'UNKNOWN'`. */
  code: string;
  /** A sentence for a practice user, not a developer. */
  message: string;
  /** True for the three §2.1 dedupe refusals — an answer, not a failure. */
  isDedupe: boolean;
  existingRequestId?: string | null;
  nextEligibleAt?: string | null;
  nextAllowedResendAt?: string | null;
}

/** Statuses that mean "the patient can still act on this link". */
export const OPEN_FORM_STATUSES: PatientFormRequestStatus[] = ['pending', 'in_progress'];

/**
 * Turn an `HttpErrorResponse` from the send/resend/revoke endpoints into something a receptionist
 * can act on.
 *
 * THE THREE DEDUPE CODES GET THREE DIFFERENT SENTENCES. A generic "could not send" toast is the
 * failure mode this function exists to prevent: each of these means something different about what
 * the practice should do next, and two of them mean the practice should do nothing at all.
 */
export function describeSendFailure(error: any, formName?: string): FormRequestFailure {
  const body = error && error.error ? error.error : error;
  const code: string = (body && body.error) || 'UNKNOWN';
  const name = formName ? `"${formName}"` : 'this form';
  const existingRequestId = (body && body.existingRequestId) || null;
  const nextEligibleAt = (body && body.nextEligibleAt) || null;
  const nextAllowedResendAt = (body && body.nextAllowedResendAt) || null;

  switch (code) {
    case 'OPEN_REQUEST':
      return {
        code, isDedupe: true, existingRequestId, nextEligibleAt,
        message: `They already have ${name} outstanding. Resend the existing request instead of `
          + `creating a second one.`,
      };
    /* §30 — the two completion codes stood here. See the documents service for why they went;
     * forms took the same treatment in the same change. */
    case 'MANUAL_SEND_DISABLED':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: `${formName ? `"${formName}"` : 'That form'} is not set up to be sent by hand. `
          + `Turn on manual sending in Settings > Forms.`,
      };
    case 'FORM_NOT_PUBLISHED':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: `${formName ? `"${formName}"` : 'That form'} is still a draft. Publish it before `
          + `sending it to a patient.`,
      };
    case 'VERSION_MISSING':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: `${formName ? `"${formName}"` : 'That form'} has no published version to send. `
          + `Publish it in Settings > Forms.`,
      };
    case 'RESEND_COOLDOWN':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt, nextAllowedResendAt,
        message: 'This form was sent very recently. Please wait before sending it again.',
      };
    case 'ALREADY_COMPLETED':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: 'This form has already been completed, so it cannot be changed.',
      };
    case 'REVOKED':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: 'This request has been revoked. Send a new one instead.',
      };
    case 'PATIENT_NOT_FOUND':
    case 'FORM_NOT_FOUND':
    case 'REQUEST_NOT_FOUND':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: (body && body.message) || 'That record no longer exists.',
      };
    default:
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: (body && body.message) || 'There was a problem sending this form. Please try again.',
      };
  }
}

@Injectable({ providedIn: 'root' })
export class PatientFormRequestService {

  private apiUrl = 'api/patient-form-requests';
  private formsUrl = 'api/custom-forms';

  private requestsSubject = new BehaviorSubject<PatientFormRequestDto[] | null>(null);
  /** The open patient's requests, newest first. `null` while loading — the panel shows a spinner. */
  patientFormRequests$ = this.requestsSubject.asObservable();

  /**
   * "Open this request" — emitted when the send dialog is refused by dedupe and the user asks to
   * be taken to the request that blocked it. The panel listens and expands that row.
   *
   * It goes through the service rather than through `patient-file.component.ts` so that the
   * sidebar action and the panel's own button both land in the same place without the patient file
   * needing to know anything about form requests.
   */
  private focusRequestSubject = new Subject<string>();
  focusRequest$ = this.focusRequestSubject.asObservable();

  private patientId?: string;

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService,
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  private authOptions() {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap((patient: any) => {
        if (patient) {
          this.patientId = patient.id;
          // `null`, not `[]`: "we have not looked yet" and "there are none" are different facts,
          // and the panel draws a spinner for the first and "No forms yet" for the second.
          this.requestsSubject.next(null);
          return this.loadRequestsByPatient(patient.id);
        }
        this.patientId = undefined;
        this.requestsSubject.next(null);
        return [];
      }),
      tap((result: any) => {
        this.requestsSubject.next(result && result.data ? result.data : []);
      }),
    ).subscribe({
      error: () => this.requestsSubject.next([]),
    });
  }

  /**
   * `patientFormRequested` / `patientFormCompleted` / `patientFormRevoked`, emitted to the group
   * room (§14.3 decision 5). The sibling panels wire their equivalents, so this one does too.
   *
   * `patientFormRequested` RELOADS rather than merges: its payload is `{ id, patientId,
   * customFormId, status }`, which has no `CustomForm` include and no `createdAt`, so splicing it
   * into the list would draw a row with a blank name and a blank date. The other two carry
   * everything that changed, so they patch in place.
   */
  private setupSocketListeners(): void {
    this.socketService.listen('patientFormRequested', 'patient-form-request-service')
      .subscribe((event: any) => {
        if (!event || !this.isCurrentPatient(event.patientId)) return;
        this.refresh();
      });

    this.socketService.listen('patientFormCompleted', 'patient-form-request-service')
      .subscribe((event: any) => {
        if (!event || !this.isCurrentPatient(event.patientId)) return;
        const patched = this.patchRequest(event.id, {
          status: 'completed',
          completedAt: event.completedAt,
          scoreTotal: event.scoreTotal,
          scoreMax: event.scoreMax,
        });
        // A completion for a request we have never seen (raised on another screen while this
        // patient was open) still belongs on the list.
        if (!patched) this.refresh();
      });

    this.socketService.listen('patientFormRevoked', 'patient-form-request-service')
      .subscribe((event: any) => {
        if (!event || !this.isCurrentPatient(event.patientId)) return;
        this.patchRequest(event.id, { status: 'revoked' });
      });
  }

  private isCurrentPatient(patientId: string | undefined): boolean {
    return !!patientId && !!this.patientId
      && String(patientId).toLowerCase() === String(this.patientId).toLowerCase();
  }

  /** Merge a partial into one row. Returns false when the row is not on the list. */
  private patchRequest(id: string, patch: Partial<PatientFormRequestDto>): boolean {
    const current = this.requestsSubject.getValue();
    if (!current || !id) return false;
    let found = false;
    const next = current.map((request) => {
      if (request.id !== id) return request;
      found = true;
      const merged: PatientFormRequestDto = { ...request };
      // Undefined fields on the socket payload must not blank out what we already hold.
      const keys = Object.keys(patch) as Array<keyof PatientFormRequestDto>;
      for (const key of keys) {
        const value = patch[key];
        if (value !== undefined) (merged as any)[key] = value;
      }
      return merged;
    });
    if (found) this.requestsSubject.next(next);
    return found;
  }

  /** Re-read the open patient's list from the server. */
  refresh(): void {
    if (!this.patientId) return;
    this.loadRequestsByPatient(this.patientId).subscribe({
      next: (result: any) => this.requestsSubject.next(result && result.data ? result.data : []),
      error: () => { /* leave the list as it stands rather than blanking a populated panel */ },
    });
  }

  /** Ask the panel to open one request. See `focusRequest$`. */
  focusRequest(id: string): void {
    if (id) this.focusRequestSubject.next(id);
  }

  /* ── HTTP ─────────────────────────────────────────────────────────────────────────────── */

  loadRequestsByPatient(patientId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/patient/${patientId}`, this.authOptions());
  }

  /** One request WITH its answers and the version they were given against (§13.2). */
  loadRequestById(id: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${id}`, this.authOptions());
  }

  createRequest(payload: CreateFormRequestPayload): Observable<any> {
    return this.http.post<any>(this.apiUrl, payload, this.authOptions())
      .pipe(tap(() => this.refresh()));
  }

  /** Mints a NEW token — the previously sent link stops working (§14.3 decision 2). */
  resendRequest(id: string, channels: string[]): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${id}/resend`, { channels }, this.authOptions())
      .pipe(tap((result: any) => {
        if (result && result.data) this.patchRequest(id, result.data);
      }));
  }

  revokeRequest(id: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${id}/revoke`, {}, this.authOptions())
      .pipe(tap((result: any) => {
        if (result && result.data) this.patchRequest(id, result.data);
      }));
  }

  /**
   * The send dialog's picker: published, active forms in the PATIENT'S practice.
   *
   * `?groupId=` is passed so a user who belongs to several practices is offered the forms of the
   * one this patient is in, rather than a merged list. The server intersects it with membership
   * and can never widen it (§14.1 decision 7).
   */
  loadSendableForms(groupId?: string): Observable<any> {
    const query = `?status=published&active=true${groupId ? `&groupId=${encodeURIComponent(groupId)}` : ''}`;
    return this.http.get<any>(`${this.formsUrl}${query}`, this.authOptions());
  }
}
